import React, { useMemo, useState, useEffect } from 'react';
import { X, Loader, Sparkles, Save, FileText, AlertCircle, ClipboardCopy, Edit3 } from 'lucide-react';
import { transcriptionService } from '../services/transcriptionService';

const TranscriptionModal = ({ call, onClose, onSave }) => {
  const [transcription, setTranscription] = useState(call.transcription || '');
  const parseSummary = (raw) => {
    if (!raw) return { summaryText: '', summaryObj: null };
    // If already an object (legacy mode), keep it
    if (typeof raw === 'object') return { summaryText: JSON.stringify(raw, null, 2), summaryObj: raw };
    const s = String(raw);
    // Try legacy JSON parse; otherwise treat as plain text summary
    try {
      const obj = JSON.parse(s);
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        return { summaryText: s, summaryObj: obj };
      }
    } catch {
      // ignore
    }
    return { summaryText: s, summaryObj: null };
  };

  const initial = parseSummary(call.summary);
  const [summaryText, setSummaryText] = useState(initial.summaryText);
  const [summaryObj, setSummaryObj] = useState(initial.summaryObj);
  const [showRawSummaryEditor, setShowRawSummaryEditor] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(''); // 'transcribing', 'summarizing', 'done'
  const [hasStarted, setHasStarted] = useState(false); // Prevent duplicate transcription
  const [isClosing, setIsClosing] = useState(false); // Auto-close indicator

  const parsedStrict = useMemo(() => {
    const text = (summaryText || '').trim();
    if (!text) return null;

    const isMeaningful = (v) => {
      if (v === null || v === undefined) return false;
      const s = String(v).trim();
      if (!s) return false;
      const norm = s.toLowerCase();
      return !['none', 'not clear', 'n/a', 'na', '-'].includes(norm);
    };

    const out = {
      customerIssue: '',
      resolution: '',
      vehicle: '',
      serviceRequired: '',
      sentiment: '',
      urgencyLevel: '',
      location: '',
      followUpRequired: '',
      agentHandling: '',
      issueInCallHandling: '',
      suggestedImprovement: '',
      keyPoints: [],
      actionItems: [],
      raw: text,
    };

    const lines = text.split(/\r?\n/).map((l) => l.trim());
    const nonEmpty = lines.filter(Boolean);
    if (!nonEmpty.length) return null;

    const sectionMap = {
      'customer issue': 'customerIssue',
      'resolution': 'resolution',
      'vehicle': 'vehicle',
      'service required': 'serviceRequired',
      'sentiment': 'sentiment',
      'urgency level': 'urgencyLevel',
      'location': 'location',
      'follow-up required': 'followUpRequired',
      'follow up required': 'followUpRequired',
      'quality check': 'qualityCheck',
      'key points': 'keyPoints',
      'action items': 'actionItems',
    };

    let current = '';
    for (const line of nonEmpty) {
      const headerMatch = line.match(/^([A-Za-z -]+):\s*$/);
      if (headerMatch) {
        const key = headerMatch[1].toLowerCase().trim();
        current = sectionMap[key] || '';
        continue;
      }

      if (current === 'keyPoints') {
        const m = line.match(/^\d+\.\s*(.+)$/);
        if (m?.[1]) out.keyPoints.push(m[1].trim());
        continue;
      }
      if (current === 'actionItems') {
        const m = line.match(/^-+\s*(.+)$/);
        if (m?.[1]) out.actionItems.push(m[1].trim());
        continue;
      }
      if (current === 'qualityCheck') {
        const m1 = line.match(/^agent handling:\s*(.+)$/i);
        const m2 = line.match(/^issue in call handling:\s*(.+)$/i);
        const m3 = line.match(/^suggested improvement:\s*(.+)$/i);
        if (m1?.[1]) out.agentHandling = m1[1].trim();
        if (m2?.[1]) out.issueInCallHandling = m2[1].trim();
        if (m3?.[1]) out.suggestedImprovement = m3[1].trim();
        continue;
      }

      if (current && typeof out[current] === 'string') {
        out[current] = out[current] ? `${out[current]}\n${line}` : line;
      }
    }

    // If it doesn't look like strict-format, don't try to render as structured
    const hasAny =
      out.customerIssue ||
      out.resolution ||
      out.serviceRequired ||
      out.keyPoints.length ||
      out.actionItems.length;

    // Normalize "NONE/NOT CLEAR" style values so UI doesn't show useless sections
    if (!isMeaningful(out.agentHandling)) out.agentHandling = '';
    if (!isMeaningful(out.issueInCallHandling)) out.issueInCallHandling = '';
    if (!isMeaningful(out.suggestedImprovement)) out.suggestedImprovement = '';

    return hasAny ? out : null;
  }, [summaryText]);

  // Auto-transcribe on mount if no transcription/summary exists
  useEffect(() => {
    // Only auto-start if no existing transcription AND no summary
    if (!hasStarted && !call.transcription && !call.summary && call.recordings && call.recordings.length > 0 && !isLoadingAI) {
      console.log('🎬 Auto-starting transcription...');
      setHasStarted(true);
      handleAutoTranscribe();
    } else if (call.transcription || call.summary) {
      console.log('✅ Transcription/summary already exists, showing saved data...');
      // Load existing data
      setTranscription(call.transcription || '');
      const parsed = parseSummary(call.summary);
      setSummaryText(parsed.summaryText);
      setSummaryObj(parsed.summaryObj);
    }
  }, [hasStarted, call.transcription, call.summary, call.recordings, isLoadingAI]);

  const handleAutoTranscribe = async () => {
    try {
      console.log('🎤 Starting automatic transcription...');
      setError('');
      setIsLoadingAI(true);
      setCurrentStep('transcribing');

      // Get audio URL
      const recordingFile = call.recordings[0].file;
      const audioUrl = `https://s-ct3.sarv.com/v2/recording/direct/11965974${recordingFile}`;
      
      console.log('📡 Audio URL:', audioUrl);

      // Step 1: Transcribe audio
      const transcriptionResult = await transcriptionService.transcribeAudio(audioUrl);
      
      if (!transcriptionResult.success) {
        setError(transcriptionResult.error + ' - Audio quality may be poor. You can try again or enter transcription manually.');
        setIsLoadingAI(false);
        setCurrentStep('');
        return;
      }

      console.log('✅ Transcription received:', transcriptionResult.transcription.substring(0, 100) + '...');
      setTranscription(transcriptionResult.transcription);

      // Step 2: Generate summary
      setCurrentStep('summarizing');
      console.log('🤖 Generating AI summary...');
      
      const summaryResult = await transcriptionService.generateSummary(transcriptionResult.transcription);
      
      if (!summaryResult.success) {
        throw new Error(summaryResult.error || 'Failed to generate summary');
      }

      console.log('✅ Summary generated:', summaryResult.summary);
      setSummaryText(summaryResult.summary || '');
      setSummaryObj(null);
      
      setCurrentStep('done');
      
      // Auto-save to database
      console.log('💾 Auto-saving to database...');
      console.log('🆔 Call ID being saved:', call.callid);
      console.log('📋 Full call object:', call);
      const saveResult = await transcriptionService.saveTranscription(
        call.callid,
        transcriptionResult.transcription,
        summaryResult.summary,
        'completed'
      );
      
      if (saveResult.success) {
        console.log('✅ Auto-saved successfully!');
        console.log('📊 Saved data:', saveResult.data);
        
        // Update local call object to reflect changes immediately
        call.transcription = transcriptionResult.transcription;
        call.summary = summaryResult.summary;
        call.transcription_status = 'completed';
        call.transcribed_at = new Date().toISOString();
        
        setIsLoadingAI(false);
        
        // Trigger parent refresh to show green button
        if (onSave) {
          console.log('🔄 Calling parent onSave to refresh call list...');
          onSave();
        } else {
          console.warn('⚠️ No onSave callback provided!');
        }
        
        // Auto-close modal after 2 seconds to show green button
        console.log('⏱️ Auto-closing modal in 2 seconds to show green button...');
        setIsClosing(true);
        setTimeout(() => {
          console.log('👋 Closing modal to reveal green button!');
          onClose();
        }, 2000);
        
        console.log('🎉 Auto-transcription, summary, and save complete!');
      } else {
        console.error('❌ Auto-save failed:', saveResult.error);
        setIsLoadingAI(false);
      }
    } catch (err) {
      console.error('💥 Error in auto-transcribe:', err);
      setError(`Error: ${err.message}`);
      setIsLoadingAI(false);
      setCurrentStep('');
    }
  };

  const handleGenerateSummary = async () => {
    if (!transcription.trim()) {
      setError('Please enter transcription text first');
      return;
    }

    console.log('🚀 Starting AI summary generation...');
    setError('');
    setIsLoadingAI(true);
    setCurrentStep('summarizing');
    
    try {
      console.log('📝 Transcription text:', transcription.substring(0, 100) + '...');
      const result = await transcriptionService.generateSummary(transcription);
      console.log('✅ AI Result:', result);
      
      if (result.success) {
        setSummaryText(result.summary || '');
        setSummaryObj(null);
        setCurrentStep('done');
        console.log('✨ Summary set successfully!');
      } else {
        console.error('❌ AI Error:', result.error);
        setError(result.error || 'Failed to generate AI summary. Please try again.');
        setCurrentStep('');
      }
    } catch (err) {
      console.error('💥 Exception in handleGenerateSummary:', err);
      setError(`Error: ${err.message}`);
      setCurrentStep('');
    }
    
    setIsLoadingAI(false);
  };

  const handleSave = async () => {
    if (!transcription.trim()) {
      setError('Transcription cannot be empty');
      return;
    }

    setError('');
    setIsSaving(true);
    const summaryToSave = summaryObj ? summaryObj : summaryText;
    const result = await transcriptionService.saveTranscription(
      call.callid,
      transcription,
      summaryToSave,
      'completed'
    );
    if (result.success) {
      onSave();
    } else {
      setError(result.error || 'Failed to save transcription and summary.');
    }
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Mobile Responsive */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
            <FileText className="text-blue-600 flex-shrink-0" size={20} />
            <div className="min-w-0 flex-1">
              <h2 className="text-base md:text-xl font-semibold text-gray-900 truncate">Call Transcription & Summary</h2>
              <p className="text-xs md:text-sm text-gray-500 mt-1 truncate">Call ID: {call.callid}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 ml-2"
            disabled={isSaving}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-300 text-red-700 px-4 py-3 rounded-lg animate-pulse">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">❌</span>
                <div>
                  <p className="font-semibold">Error</p>
                  <p>{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoadingAI && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Loader className="animate-spin text-purple-600" size={28} />
                <div className="flex-1">
                  {currentStep === 'transcribing' && (
                    <>
                      <p className="font-semibold text-purple-700 text-lg">🎤 Transcribing Audio...</p>
                      <p className="text-sm text-purple-600 mt-1">Converting speech to text using OpenAI Whisper</p>
                      <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                        <div className="bg-purple-600 h-2 rounded-full animate-pulse" style={{ width: '50%' }}></div>
                      </div>
                    </>
                  )}
                  {currentStep === 'summarizing' && (
                    <>
                      <p className="font-semibold text-blue-700 text-lg">🤖 Generating AI Summary...</p>
                      <p className="text-sm text-blue-600 mt-1">Analyzing transcription with GPT-4o-mini</p>
                      <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                        <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '75%' }}></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Transcription (cleaned) - hidden by default */}
          {showTranscript && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Transcription (cleaned)</h3>
                  <p className="text-xs text-gray-500">Hidden by default. You can edit this before saving.</p>
                </div>
              </div>
              <textarea
                value={transcription}
                onChange={(e) => setTranscription(e.target.value)}
                rows={6}
                className="w-full text-sm border border-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Transcription will appear here..."
              />
            </div>
          )}

          {/* Summary actions */}
          {!isLoadingAI && !summaryText && transcription && (
            <div>
              <button
                onClick={handleGenerateSummary}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                <Sparkles size={18} />
                <span>Generate AI Summary</span>
              </button>
            </div>
          )}
          
          {/* Summary (new text format) */}
          {!summaryObj && summaryText && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">AI Summary (strict format)</h3>
                  <p className="text-xs text-gray-500">Editable. Saved in <code className="font-mono">call_reports.summary</code>.</p>
                </div>
                <div className="flex items-center gap-2">
                  {!isLoadingAI && call.recordings && call.recordings.length > 0 && (
                    <button
                      type="button"
                      onClick={handleAutoTranscribe}
                      className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50"
                      title="Re-transcribe (clean transcript) + regenerate summary"
                    >
                      Re-transcribe
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(summaryText || '')}
                    className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50"
                    title="Copy summary"
                  >
                    <span className="inline-flex items-center gap-1">
                      <ClipboardCopy size={14} />
                      Copy
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTranscript((v) => !v)}
                    className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50"
                    title="Show/Hide cleaned transcription"
                  >
                    {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRawSummaryEditor((v) => !v)}
                    className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50"
                    title="Edit raw summary text"
                  >
                    <span className="inline-flex items-center gap-1">
                      <Edit3 size={14} />
                      {showRawSummaryEditor ? 'Hide Edit' : 'Edit'}
                    </span>
                  </button>
                  <button
                    onClick={handleGenerateSummary}
                    disabled={!transcription.trim() || isLoadingAI}
                    className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                    title="Regenerate summary from the transcription"
                  >
                    Regenerate
                  </button>
                </div>
              </div>

              {/* Point-wise structured view (preferred) */}
              {parsedStrict ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-gray-200 p-3 bg-red-50">
                      <p className="text-xs font-semibold text-red-700">Customer Issue</p>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.customerIssue || 'NOT CLEAR'}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3 bg-green-50">
                      <p className="text-xs font-semibold text-green-700">Resolution</p>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.resolution || 'NOT CLEAR'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-[11px] font-semibold text-gray-600">Vehicle</p>
                      <p className="text-sm font-medium text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.vehicle || 'NOT CLEAR'}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-[11px] font-semibold text-gray-600">Service Required</p>
                      <p className="text-sm font-medium text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.serviceRequired || 'NOT CLEAR'}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-[11px] font-semibold text-gray-600">Sentiment</p>
                      <p className="text-sm font-medium text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.sentiment || 'NOT CLEAR'}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-[11px] font-semibold text-gray-600">Urgency</p>
                      <p className="text-sm font-medium text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.urgencyLevel || 'NOT CLEAR'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-xs font-semibold text-gray-700">Key Points</p>
                      {parsedStrict.keyPoints?.length ? (
                        <ol className="mt-2 list-decimal list-inside space-y-1 text-sm text-gray-900">
                          {parsedStrict.keyPoints.slice(0, 8).map((p, idx) => (
                            <li key={idx} className="whitespace-pre-wrap">{p}</li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-sm text-gray-500 mt-2">NOT CLEAR</p>
                      )}
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-xs font-semibold text-gray-700">Action Items</p>
                      {parsedStrict.actionItems?.length ? (
                        <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-900">
                          {parsedStrict.actionItems.slice(0, 8).map((a, idx) => (
                            <li key={idx} className="whitespace-pre-wrap">{a}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 mt-2">NOT CLEAR</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-[11px] font-semibold text-gray-600">Location</p>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.location || 'NOT CLEAR'}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-[11px] font-semibold text-gray-600">Follow-up Required</p>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.followUpRequired || 'NOT CLEAR'}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-3">
                      <p className="text-[11px] font-semibold text-gray-600">Agent Handling</p>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.agentHandling || 'NOT CLEAR'}</p>
                    </div>
                  </div>

                  {(parsedStrict.issueInCallHandling || parsedStrict.suggestedImprovement) && (
                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-700">Quality Notes</p>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="text-[11px] font-semibold text-gray-600">Issue in Call Handling</p>
                          <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.issueInCallHandling || 'NOT CLEAR'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold text-gray-600">Suggested Improvement</p>
                          <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{parsedStrict.suggestedImprovement || 'NOT CLEAR'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap">
                  {summaryText}
                </div>
              )}

              {/* Raw editor (optional) */}
              {showRawSummaryEditor && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Raw Summary (edit)</p>
                  <textarea
                    value={summaryText}
                    onChange={(e) => setSummaryText(e.target.value)}
                    rows={14}
                    className="w-full text-sm border border-gray-200 rounded-lg p-3 font-mono whitespace-pre-wrap focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="AI Summary will appear here..."
                  />
                </div>
              )}
            </div>
          )}

          {/* Legacy JSON summary UI (backward compatible) */}
          {summaryObj && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-2xl overflow-hidden">
              {/* Minimal Header */}
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-lg">
                    <Sparkles size={18} className="text-white" />
                  </div>
                  <span className="text-white font-bold text-sm">AI Call Analysis</span>
                </div>
                {summaryObj.urgencyLevel && (
                  <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                    summaryObj.urgencyLevel.toLowerCase() === 'high' ? 'bg-red-500 text-white animate-pulse' :
                    summaryObj.urgencyLevel.toLowerCase() === 'medium' ? 'bg-yellow-400 text-gray-900' :
                    'bg-green-500 text-white'
                  }`}>
                    {summaryObj.urgencyLevel.toUpperCase()}
                  </span>
                )}
              </div>

              {/* Bento Grid Layout */}
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 auto-rows-fr">

                {/* LARGE: Main Customer Issue - Takes 4 columns, 2 rows */}
                {summaryObj.customerIssue && (
                  <div className="col-span-4 row-span-2 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl p-5 text-white shadow-xl relative overflow-hidden group hover:shadow-2xl transition-shadow">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                    <div className="relative z-10">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-3xl">🚨</span>
                        <span className="text-sm font-bold uppercase tracking-wider opacity-90">Customer Issue</span>
                      </div>
                      <p className="text-base font-semibold leading-relaxed">{summaryObj.customerIssue}</p>
                    </div>
                    <div className="absolute bottom-0 right-0 w-20 h-20 bg-white/5 rounded-tl-full"></div>
                  </div>
                )}

                {/* MEDIUM: Vehicle Info - 2 columns, 1 row */}
                {summaryObj.carModel && (
                  <div className="col-span-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-2xl">🚗</span>
                      <span className="text-xs font-bold uppercase tracking-wide opacity-90">Vehicle</span>
                    </div>
                    <p className="text-sm font-bold">{summaryObj.carModel}</p>
                  </div>
                )}

                {/* MEDIUM: Service - 2 columns, 1 row */}
                {summaryObj.serviceOffered && (
                  <div className="col-span-2 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-2xl">🔧</span>
                      <span className="text-xs font-bold uppercase tracking-wide opacity-90">Service</span>
                    </div>
                    <p className="text-sm font-bold">{summaryObj.serviceOffered}</p>
                  </div>
                )}

                {/* LARGE: Resolution - 4 columns, 2 rows */}
                {summaryObj.resolution && (
                  <div className="col-span-4 row-span-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 text-white shadow-xl relative overflow-hidden hover:shadow-2xl transition-shadow">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mt-16"></div>
                    <div className="relative z-10">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-3xl">✅</span>
                        <span className="text-sm font-bold uppercase tracking-wider opacity-90">Resolution</span>
                      </div>
                      <p className="text-base font-semibold leading-relaxed">{summaryObj.resolution}</p>
                    </div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-tr-full"></div>
                  </div>
                )}

                {/* MEDIUM: Sentiment - 2 columns, 2 rows */}
                {summaryObj.customerSentiment && (
                  <div className={`col-span-2 row-span-2 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden ${
                    summaryObj.customerSentiment.toLowerCase().includes('positive') ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
                    summaryObj.customerSentiment.toLowerCase().includes('negative') ? 'bg-gradient-to-br from-red-400 to-pink-500' :
                    'bg-gradient-to-br from-yellow-400 to-orange-500'
                  }`}>
                    <div className="absolute top-0 right-0 text-9xl opacity-10">
                      {summaryObj.customerSentiment.toLowerCase().includes('positive') ? '😊' :
                       summaryObj.customerSentiment.toLowerCase().includes('negative') ? '😔' : '😐'}
                    </div>
                    <div className="relative z-10">
                      <span className="text-xs font-bold uppercase tracking-wide opacity-90 block mb-2">Sentiment</span>
                      <p className="text-2xl font-bold mb-1">
                        {summaryObj.customerSentiment.toLowerCase().includes('positive') ? '😊' :
                         summaryObj.customerSentiment.toLowerCase().includes('negative') ? '😔' : '😐'}
                      </p>
                      <p className="text-sm font-bold">{summaryObj.customerSentiment}</p>
                    </div>
                  </div>
                )}

                {/* TALL: Key Points - 3 columns, 3 rows */}
                {summaryObj.keyPoints && summaryObj.keyPoints.length > 0 && (
                  <div className="col-span-3 row-span-3 bg-white rounded-xl p-4 shadow-lg border-2 border-blue-200 hover:border-blue-400 transition-colors">
                    <div className="flex items-center space-x-2 mb-3 pb-2 border-b-2 border-blue-100">
                      <span className="text-2xl">🔑</span>
                      <span className="text-sm text-blue-700 font-bold">Key Points</span>
                    </div>
                    <ul className="space-y-2.5">
                      {summaryObj.keyPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start space-x-2 group">
                          <span className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-lg flex items-center justify-center text-xs font-bold shadow-sm group-hover:scale-110 transition-transform">
                            {idx + 1}
                          </span>
                          <span className="text-xs text-gray-700 leading-relaxed flex-1 font-medium">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* TALL: Action Items - 3 columns, 3 rows */}
                {summaryObj.actionItems && summaryObj.actionItems.length > 0 && (
                  <div className="col-span-3 row-span-3 bg-white rounded-xl p-4 shadow-lg border-2 border-orange-200 hover:border-orange-400 transition-colors">
                    <div className="flex items-center space-x-2 mb-3 pb-2 border-b-2 border-orange-100">
                      <span className="text-2xl">📋</span>
                      <span className="text-sm text-orange-700 font-bold">Action Items</span>
                    </div>
                    <ul className="space-y-2.5">
                      {summaryObj.actionItems.map((action, idx) => (
                        <li key={idx} className="flex items-start space-x-2 group">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-3 h-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-md shadow-sm group-hover:scale-110 transition-transform"></div>
                          </div>
                          <span className="text-xs text-gray-700 leading-relaxed flex-1 font-medium">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* SMALL: Location - 2 columns, 1 row */}
                {summaryObj.locationMentioned && summaryObj.locationMentioned !== 'Not mentioned' && (
                  <div className="col-span-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg p-3 border border-purple-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-1.5 mb-1">
                      <span className="text-lg">📍</span>
                      <span className="text-xs text-purple-700 font-bold">Location</span>
                    </div>
                    <p className="text-xs text-gray-800 font-semibold truncate">{summaryObj.locationMentioned}</p>
                  </div>
                )}

                {/* SMALL: Payment - 2 columns, 1 row */}
                {summaryObj.paymentDiscussed && summaryObj.paymentDiscussed !== 'Not discussed' && (
                  <div className="col-span-2 bg-gradient-to-br from-green-100 to-teal-100 rounded-lg p-3 border border-green-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-1.5 mb-1">
                      <span className="text-lg">💰</span>
                      <span className="text-xs text-green-700 font-bold">Payment</span>
                    </div>
                    <p className="text-xs text-gray-800 font-semibold truncate">{summaryObj.paymentDiscussed}</p>
                  </div>
                )}

                {/* SMALL: ETA - 2 columns, 1 row */}
                {summaryObj.estimatedTime && summaryObj.estimatedTime !== 'Not mentioned' && (
                  <div className="col-span-2 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-lg p-3 border border-indigo-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-1.5 mb-1">
                      <span className="text-lg">⏱️</span>
                      <span className="text-xs text-indigo-700 font-bold">ETA</span>
                    </div>
                    <p className="text-xs text-gray-800 font-semibold truncate">{summaryObj.estimatedTime}</p>
                  </div>
                )}

                {/* WIDE: Follow-up Alert - 6 columns, 1 row */}
                {summaryObj.followUpRequired && (
                  <div className="col-span-6 bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 rounded-xl p-3 shadow-lg">
                    <div className="flex items-center justify-center space-x-3">
                      <div className="bg-white/30 backdrop-blur-sm p-2 rounded-lg">
                        <AlertCircle size={20} className="text-white" />
                      </div>
                      <span className="text-sm font-bold text-white uppercase tracking-wider">⚠️ Follow-up Required</span>
                    </div>
                  </div>
                )}
                
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Compact Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500">
            {isClosing ? (
              <span className="text-blue-600 font-semibold flex items-center space-x-1 animate-pulse">
                <span>🔄</span>
                <span>Closing to show green button...</span>
              </span>
            ) : (call.transcription || call.summary) && !isLoadingAI && currentStep !== 'done' ? (
              <span className="text-green-600 font-semibold flex items-center space-x-1">
                <span>✓</span>
                <span>Previously saved in database</span>
              </span>
            ) : (summaryText || summaryObj) && !isLoadingAI ? (
              <span className="text-green-600 font-semibold flex items-center space-x-1">
                <span>✓</span>
                <span>Auto-saved to database</span>
              </span>
            ) : null}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onClose}
              disabled={isClosing}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                isClosing 
                  ? 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed' 
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {isClosing ? 'Closing...' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptionModal;
